import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { getBlogSidebar, monthLabel, thumbOf } from "../../lib/blogApi";
import type { BlogCard, BlogSidebar as Data } from "../../lib/blogApi";
import { assetUrl } from "../../lib/api";
import { longDate } from "../../lib/format";
import { useLang, useT } from "../../i18n";

function Box({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-line-strong bg-white p-4">
      <h2 className="m-0 mb-3 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink">{title}</h2>
      {children}
    </section>
  );
}

/** Small thumbnail + title + one line (date or views) */
function MiniList({ items, line }: { items: BlogCard[]; line: (a: BlogCard) => string }) {
  return (
    <ul className="m-0 grid list-none gap-3 p-0">
      {items.map((a) => (
        <li key={a.slug}>
          <Link to={`/blog/${a.slug}`} className="group flex gap-3 text-ink no-underline">
            <span className="h-14 w-[72px] shrink-0 overflow-hidden rounded bg-[#E4E4E4]">
              {a.coverImage && <img src={assetUrl(thumbOf(a.coverImage))} alt="" loading="lazy" className="h-full w-full object-cover" />}
            </span>
            <span className="min-w-0">
              <span className="line-clamp-2 text-[13px] font-medium leading-snug group-hover:underline">{a.title}</span>
              <span className="mt-0.5 block text-[11px] text-faint">{line(a)}</span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** Right column of the blog: vehicle search, latest, most read, categories, archives, tags. */
export default function BlogSidebar() {
  const t = useT();
  const b = t.blog;
  const lang = useLang();
  const [data, setData] = useState<Data | null>(null);

  useEffect(() => {
    let cancelled = false;
    getBlogSidebar().then(
      (d) => !cancelled && setData(d),
      () => !cancelled && setData({ latest: [], popular: [], categories: [], archives: [], tags: [] }),
    );
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const row = "flex items-center justify-between border-b border-line py-2 text-[13px] text-ink no-underline last:border-0 hover:text-brand-link";

  return (
    <aside className="grid content-start gap-4">
      <section className="rounded-lg bg-ink p-5 text-white">
        <h2 className="m-0 text-[11px] font-semibold uppercase tracking-[0.1em]">{b.findTitle}</h2>
        <p className="mt-2 mb-4 text-[13px] leading-relaxed text-white/75">{b.findText}</p>
        <Link to="/vehicles" className="flex h-9 items-center justify-center rounded bg-brand text-[13px] font-medium text-white no-underline hover:bg-brand-dark">
          {b.findButton}
        </Link>
      </section>

      {data === null ? (
        <div className="h-64 animate-pulse rounded-lg bg-surface" aria-busy="true" />
      ) : (
        <>
          {data.latest.length > 0 && (
            <Box title={b.latest}>
              <MiniList items={data.latest} line={(a) => longDate(a.publishedAt.slice(0, 10))} />
            </Box>
          )}
          {data.popular.length > 0 && (
            <Box title={b.popular}>
              <MiniList items={data.popular} line={(a) => b.views(a.views)} />
            </Box>
          )}
          {data.categories.length > 0 && (
            <Box title={b.categories}>
              <nav className="grid">
                {data.categories.map((c) => (
                  <Link key={c.slug} to={`/blog?category=${c.slug}`} className={row}>
                    {c.name}
                    <span className="text-xs text-faint">{c.count}</span>
                  </Link>
                ))}
              </nav>
            </Box>
          )}
          {data.archives.length > 0 && (
            <Box title={b.archives}>
              <nav className="grid">
                {data.archives.map((m) => (
                  <Link key={m.month} to={`/blog?month=${m.month}`} className={row}>
                    {monthLabel(m.month, t.format.locale)}
                    <span className="text-xs text-faint">{m.count}</span>
                  </Link>
                ))}
              </nav>
            </Box>
          )}
          {data.tags.length > 0 && (
            <Box title={b.tags}>
              <div className="flex flex-wrap gap-1.5">
                {data.tags.map((tg) => (
                  <Link key={tg.tag} to={`/blog/tag/${tg.slug}`} className="rounded bg-surface px-2 py-1 text-xs text-ink no-underline hover:bg-surface-hover">
                    {tg.tag}
                  </Link>
                ))}
              </div>
            </Box>
          )}
        </>
      )}
    </aside>
  );
}
